import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {BehaviorSubject, combineLatest, Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';
import {AnalyticsService} from './analytics.service';
import {AuthService} from './auth.service';
import {StorageService} from './storage.service';
import {TourStepTargetDirective} from './tour-step-target.directive';

type StepDefinition = string | (() => void | string | Promise<any>);

export interface TourState {
  currentStep: string | null;
  index: number | null;
}

export interface StepState {
  currentTour: string | null;
  active: boolean;
}

export interface TargetConfig {
  name: string;
  attachment?: string;
  component?: TourStepTargetDirective;
}

const STORAGE_PREFIX = 'tourComplete.';

function defaultState<T>(obj: {[name: string]: BehaviorSubject<T>}, name: string, defaultVal: T): BehaviorSubject<T> {
  return obj[name] ? obj[name] : (obj[name] = new BehaviorSubject<T>(defaultVal));
}

@Injectable()
export class TourService {

  tours: {[name: string]: StepDefinition[]} = {
    requestTalentHeader: [
      'rtLearnMore',
      () => (this.Auth.isFullyAuthenticated()
        && this.Auth.getAuthType() !== 'timecard')
        ? this.Router.navigate(['/request'])
        : 'rtLearnMore'
    ]
  };

  private tourState: {[name: string]: BehaviorSubject<TourState>} = {};
  private stepState: {[name: string]: BehaviorSubject<StepState>} = {};
  private stepTargets: {[name: string]: BehaviorSubject<TargetConfig>} = {};

  constructor(private Auth: AuthService,
              private Router: Router,
              private Storage: StorageService,
              private Analytics: AnalyticsService) {
  }

  async startTour(tour: string): Promise<boolean> {
    if (this.Storage.get(STORAGE_PREFIX + tour, false)) return;
    if (this.getTourState(tour).getValue().index === null) {
      this.Analytics.trackEvent(`Tour ${tour}: started`);
      await this.nextStep(tour);
      return true;
    }
    return false;
  }

  stopTour(tour: string, complete?: boolean) {
    const tourSubject = this.getTourState(tour);
    const tourState = tourSubject.getValue();
    if (tourState.index !== null) {
      if (complete && tourState.index === this.tours[tour].length - 1) {
        this.Analytics.trackEvent(`Tour ${tour}: completed`);
      } else if (complete) {
        this.Analytics.trackEvent(`Tour ${tour}: partially completed`);
      } else {
        this.Analytics.trackEvent(`Tour ${tour}: closed`);
      }
    }
    if (tourState.currentStep) {
      const stepSubject = this.getStepState(tourState.currentStep);
      stepSubject.next({...stepSubject.getValue(), currentTour: null, active: false});
    }
    tourSubject.next({currentStep: null, index: null});
    if (complete || complete === undefined) this.Storage.put(STORAGE_PREFIX + tour, true);
  }

  async nextStep(tour: string) {
    const tourSubject = this.getTourState(tour);
    const tourState = tourSubject.getValue();
    if (tourState.currentStep) {
      const stepSubject = this.getStepState(tourState.currentStep);
      stepSubject.next({...stepSubject.getValue(), currentTour: null, active: false});
    }
    for (let index = tourState.index === null ? 0 : tourState.index + 1; index < this.tours[tour].length; ++index) {
      const step = await this.resolveStep(tour, index);
      if (step) {
        const stepSubject = this.getStepState(step);
        stepSubject.next({...stepSubject.getValue(), currentTour: tour, active: true});
        tourSubject.next({currentStep: step, index});
        return;
      }
    }
    // End of tour
    tourSubject.next({currentStep: null, index: this.tours[tour].length - 1});
    this.stopTour(tour, true);
  }

  registerStepTarget(config: TargetConfig) {
    this.getStepTarget(config.name).next(config);
  }

  deregisterStepTarget(step: string, component: TourStepTargetDirective) {
    const subject = this.getStepTarget(step);
    if (subject.getValue().component === component) {
      subject.next(null);
    }
  }

  private getTourState(tour: string): BehaviorSubject<TourState> {
    return defaultState(this.tourState, tour, {
      currentStep: null,
      index: null
    });
  }

  getStepState(step: string): BehaviorSubject<StepState> {
    return defaultState(this.stepState, step, {
      currentTour: null,
      active: false
    });
  }

  private getStepTarget(step: string): BehaviorSubject<TargetConfig> {
    return defaultState(this.stepTargets, step, {name: step});
  }

  getStepTargets(tour: string, step: string | string[] | {[tour: string]: string | string[]}): Observable<TargetConfig | null> {
    if (typeof step === 'string') {
      return this.getStepTarget(step);
    } else if (Array.isArray(step)) {
      return combineLatest(step.map(n => this.getStepTarget(n)))
        .pipe(map((vals: TargetConfig[]) => vals.find(v => v && !!v.component) || null));
    } else if (tour) {
      return this.getStepTargets(tour, step[tour]);
    }
    return of(null);
  }

  private resolveStep(tour: string, index: number): Promise<string> {
    let step: StepDefinition | null;
    if (this.tours.hasOwnProperty(tour)
      && (step = this.tours[tour][index])) {
      if (typeof step === 'function') {
        return Promise.resolve(step())
          .then(val => typeof val === 'string' ? val : null);
      }
    }
    return Promise.resolve(step as string);
  }
}
